/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React from 'react';
import PropTypes from 'prop-types';
import { usePermissions } from '../context/PermissionContext';

/**
 * A component that conditionally renders its children based on user permissions.
 * Uses the global PermissionContext for consistent permission checking.
 * 
 * @param {Object} props
 * @param {string[]} props.permissions - Array of permission keys required to see the children.
 * @param {boolean} [props.requireAll=false] - Whether all permissions are required.
 * @param {React.ReactNode} [props.fallback=null] - What to render if the user doesn't have permission.
 * @param {React.ReactNode} props.children - The content to gate.
 */
const PermissionGuard = ({
  permissions = [],
  requireAll = false,
  fallback = null,
  children
}) => {
  const { hasPermission } = usePermissions();

  // If no permissions are specified, just render children
  if (!permissions || permissions.length === 0) {
    return <>{children}</>;
  }

  // Check if user has required permissions
  const permitted = hasPermission(permissions, requireAll);

  if (!permitted) {
    return fallback;
  }

  return <>{children}</>;
};

export default PermissionGuard;

PermissionGuard.propTypes = {
  permissions: PropTypes.arrayOf(PropTypes.string),
  requireAll: PropTypes.bool,
  fallback: PropTypes.node,
  children: PropTypes.node.isRequired,
};
